import { forwardRef, SelectHTMLAttributes } from 'react';

/**
 * Select component matching Bootstrap .form-control styles for selects EXACTLY
 *
 * Source of truth from bootstrap-3.4.1.min.css:
 *
 * select.form-control {
 *   display: block;
 *   width: 100%;
 *   height: 34px;
 *   padding: 6px 12px;
 *   font-size: 14px;
 *   line-height: 1.42857143;
 *   color: #555;
 *   background-color: #fff;
 *   background-image: none;
 *   border: 1px solid #ccc;
 *   border-radius: 4px;
 *   box-shadow: inset 0 1px 1px rgba(0,0,0,.075);
 *   transition: border-color ease-in-out .15s, box-shadow ease-in-out .15s;
 * }
 *
 * NO CHANGES to logic, behavior, or structure - only visual parity.
 */

export interface UiSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  hasError?: boolean;
}

export const UiSelect = forwardRef<HTMLSelectElement, UiSelectProps>(
  ({ hasError, className = '', children, ...props }, ref) => {
    // Base classes matching .form-control from Bootstrap
    const baseClasses = [
      'block', // display: block
      'w-full', // width: 100%
      'h-angular-input', // height: 34px
      'px-3', // padding: 6px 12px (horizontal = 12px)
      'py-[6px]', // padding: 6px 12px (vertical = 6px)
      'text-angular-base', // font-size: 14px
      'leading-angular-base', // line-height: 1.42857143
      'text-[#555]', // color: #555
      'bg-white', // background-color: #fff
      'border', // border: 1px solid
      'border-solid',
      'rounded-angular', // border-radius: 4px
      'transition-all', // transition: border-color, box-shadow ease-in-out .15s
      'duration-150',
      'ease-in-out',
    ].join(' ');

    const borderClasses = hasError
      ? 'border-angular-error-highlight'
      : 'border-angular-border-input focus:border-angular-focus';

    const shadowClasses = hasError
      ? 'focus:shadow-angular-input-error-focus'
      : 'shadow-angular-input focus:shadow-angular-input-focus';

    const disabledClasses = props.disabled
      ? '!bg-angular-bg-disabled !text-[#555] !cursor-not-allowed !opacity-100 !border-angular-border-input'
      : '';

    const focusClasses = 'focus:outline-none';

    return (
      <select
        ref={ref}
        aria-invalid={hasError || undefined}
        className={`${baseClasses} ${borderClasses} ${shadowClasses} ${disabledClasses} ${focusClasses} ${className}`}
        {...props}
      >
        {children}
      </select>
    );
  }
);

UiSelect.displayName = 'UiSelect';
